import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
// Utils
import { Form, FormElements } from '@zolteam/zol-react-form';
// Components
import { Button, Skeleton } from '@zolteam/zol-admin-ui';
// Services
import { useGetAdminUserQuery } from '../../services/parazol/parazolQueries';
import { postActivateAdminUser, postDeactivateAdminUser } from '../../services/parazol/parazolService';
// Context
import { useAppContext } from '../../store/AppContext';
// Locales
import fr from '../../locales/fr.json';

const FORM_NAME = 'user-activate';

const SCHEMA = {
  $schema: 'https://json-schema.org/draft/2020-12/schema',
  type: 'object',
  title: 'UtilisateurActivate',
  properties: {
    isActive: {
      type: 'boolean',
      description: 'Activer/Désactiver',
      x_layout: '1/1/1/7',
    },
  },
  required: ['isActive'],
  additionalProperties: false,
};

/**
 * UserActivate component.
 * @returns {JSX.Element}
 */
function UserActivate() {
  // Hooks
  const { t } = useTranslation();
  const { id: userId } = useParams();
  const [, dispatch] = useAppContext();

  // Services
  const {
    isLoading: isLoadingUser,
    data: userData,
    refetch: getAdminUserRefetch,
  } = useGetAdminUserQuery(
    userId,
  );
  const postActivateAdminUserMutation = useMutation({
    mutationFn: (/** @type {number} */version) => postActivateAdminUser(
      userId,
      version,
    ),
    onSuccess: () => {
      dispatch({
        type: 'SET_MESSAGE',
        payload: { text: t('parazol.userForm.activateSuccess'), type: 'success' },
      });
      getAdminUserRefetch();
    },
  });
  const postDeactivateAdminUserMutation = useMutation({
    mutationFn: (/** @type {number} */version) => postDeactivateAdminUser(
      userId,
      version,
    ),
    onSuccess: () => {
      dispatch({
        type: 'SET_MESSAGE',
        payload: { text: t('parazol.userForm.deactivateSuccess'), type: 'success' },
      });
      getAdminUserRefetch();
    },
  });

  // Handlers
  const onSubmit = (/** @type {{isActive:boolean}} */values) => {
    if (values.isActive) {
      postActivateAdminUserMutation.mutate(userData.version);
    } else {
      postDeactivateAdminUserMutation.mutate(userData.version);
    }
  };

  return (
    <>
      <h1 className="h1-default margin-top-m margin-bottom-m" style={{ fontWeight: 'bold' }}>
        {t('parazol.userForm.activateUserTitle')}
      </h1>
      {isLoadingUser && !userData
        ? <Skeleton shape="rectangle" height="3.625rem" width="100%" />
        : (
          <Form
            name={FORM_NAME}
            className=""
            callbacks={{
              onSubmit,
            }}
            defaultValues={{ isActive: userData.isActive }}
            schema={SCHEMA}
            translations={{
              formValidation: fr.cortizol.formValidation,
            }}
          >
            {(formConfig, formInfo) => (
              <FormElements
                formConfig={formConfig}
                formTitle={formInfo.formName}
                translations={fr.cortizol}
              />
            )}
          </Form>
        )}

      <Button
        form={FORM_NAME}
        isSubmit
        isLoading={
          postActivateAdminUserMutation.isLoading
          || postDeactivateAdminUserMutation.isLoading
          }
      >
        {t('parazol.userForm.submit')}
      </Button>
    </>
  );
}

export default UserActivate;
